/* eslint-disable no-console */
import { toastNotifications } from '@uag/react-core';
import { t } from 'i18next';
import {
    HubConnectionBuilder,
    HttpTransportType,
    LogLevel,
    HubConnectionState,
    type IRetryPolicy,
    type RetryContext
} from '@microsoft/signalr';

import { HubConnectionEvent } from 'app/modules/wallforming/components/content-container/types/hubConnectionEvent';
import { PlacementUpdateEvent } from 'app/modules/wallforming/components/content-container/types/placementUpdateEvent';
import {
    mapIndexToArticleLifeCycle,
    mapPlacementToArticleInstance
} from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleUtils';
import { removeCachedInstanceEdges } from 'app/modules/wallforming/components/scenes/objects/articles/utils/edgesOverlayUtils';
import { getRelatedPoiIndices } from 'app/modules/wallforming/components/scenes/objects/pois/utils/poiUtils';
import { EMPTY_GUID } from 'shared/constants';

import type { ArticleGroupPlacement, ArticleInstancePlacement, PartPlacement } from 'api/models';
import type { ArticleInstance } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleInstance';
import type { ConnectionSliceState } from 'app/modules/wallforming/components/state-management/placement/states/connectionSliceState';
import type { PlacementState } from 'app/modules/wallforming/components/state-management/placement/types';
import type { StateCreator } from 'zustand';

const DEBUG_CONNECTION = true;
const TIMEOUT_DELAY_MS = 5_000;
const FLOATING_POINTS_THRESHOLD = 1e-4;

const debug = (...args: unknown[]) => {
    if (DEBUG_CONNECTION) {
        console.log(...args);
    }
};

const indefiniteRetryPolicy: IRetryPolicy = {
    nextRetryDelayInMilliseconds(retryContext: RetryContext): number | null {
        const attempt = retryContext.previousRetryCount;
        if (attempt < 3) {
            return 0;
        }
        if (attempt < 6) {
            return 2_000;
        }
        if (attempt < 10) {
            return 5_000;
        }
        if (attempt < 20) {
            return 10_000;
        }
        return 30_000;
    }
};

// prevent reconnect loop on intentional stop
let isIntentionalStop = false;
let reconnectTimerHandle: ReturnType<typeof setTimeout> | undefined;

export let isBatchProcessing = false;

const batchCompleteListeners = new Set<(pendingInstanceIds: ReadonlySet<string>) => void>();

export const onBatchComplete = (cb: (pendingInstanceIds: ReadonlySet<string>) => void): (() => void) => {
    batchCompleteListeners.add(cb);
    return () => {
        batchCompleteListeners.delete(cb);
    };
};

export const startBatchProcessing = () => {
    isBatchProcessing = true;
};

export const finishBatchProcessing = () => {
    isBatchProcessing = false;
    const pendingInstanceIds = pendingBatchInstanceIds;
    pendingBatchInstanceIds = new Set<string>();
    batchCompleteListeners.forEach((cb) => cb(pendingInstanceIds));
};

// Buffer for article groups that were received before POIs are loaded
let bufferedArticleGroups: ArticleGroupPlacement[] = [];
let pendingBatchInstanceIds = new Set<string>();

export function placeBufferedArticleGroups(
    set: (partial: Partial<PlacementState> | ((state: PlacementState) => Partial<PlacementState>)) => void,
    get: () => PlacementState
) {
    const state = get();
    if (state.pois && state.pois.length > 0 && bufferedArticleGroups.length > 0) {
        // Process all buffered article groups
        processBatch([...bufferedArticleGroups], set, get);
        bufferedArticleGroups = [];
    }
}

const processBatch = (
    articleGroups: ArticleGroupPlacement[],
    set: (partial: Partial<PlacementState> | ((state: PlacementState) => Partial<PlacementState>)) => void,
    get: () => PlacementState
): void => {
    isBatchProcessing = true;

    const totalCount = articleGroups.reduce((sum, g) => sum + g.instances.length, 0);
    debug('Processing batch', { groups: articleGroups.length, totalInstances: totalCount });

    const state = get();

    // Fallback: buffer the received article groups placements until POIs are loaded
    if (!state.pois || state.pois.length === 0) {
        bufferedArticleGroups.push(...articleGroups);
        debug('POIs not loaded, buffering article groups:', 'Total buffered:', bufferedArticleGroups.length);
        isBatchProcessing = false;
        return;
    }

    const result = processArticleGroups(articleGroups, state);

    const articlesChanged = result.articles !== state.articles;
    const poisChanged = result.pois !== state.pois;

    if (!articlesChanged && !poisChanged) {
        finishBatchProcessing();
        return;
    }

    if (poisChanged) {
        set({ pois: result.pois });
    }

    if (articlesChanged) {
        for (const articleGroup of articleGroups) {
            for (const instance of articleGroup.instances) {
                pendingBatchInstanceIds.add(instance.id);
            }
        }

        requestAnimationFrame(() => {
            const articleInstanceIds = new Set(articleGroups.flatMap((g) => g.instances.map((i) => i.id)));

            set({
                placementUpdate: {
                    event: PlacementUpdateEvent.PLACE,
                    articleInstanceIds
                }
            });
        });
        set((state) => {
            // Update drag references for all affected articles
            const dragRefsMap = state.articleDragRefsMap?.current;
            if (dragRefsMap) {
                for (const [articleId, dragRef] of dragRefsMap.entries()) {
                    const updatedArticle = result.articles[articleId];
                    if (dragRef?.articleRef?.current && updatedArticle) {
                        dragRef.articleRef.current.articleInstances = { ...updatedArticle.articleInstances };
                    }
                }
            }
            return { articles: result.articles };
        });
    }

    finishBatchProcessing();
};

// Compares only a specific amount of float values to ignore precision noise between the systems
const areEqual = (a: number, b: number): boolean => Math.abs(a - b) <= FLOATING_POINTS_THRESHOLD;

const arePartPlacementsEqual = (currentPart: ArticleInstance['parts'][string], nextPart: PartPlacement): boolean => {
    return (
        areEqual(currentPart.rotation, nextPart.rotation) &&
        areEqual(currentPart.position.x, nextPart.position.x) &&
        areEqual(currentPart.position.y, nextPart.position.y) &&
        areEqual(currentPart.position.z, nextPart.position.z) &&
        areEqual(currentPart.alignment.x, nextPart.alignment.x) &&
        areEqual(currentPart.alignment.y, nextPart.alignment.y) &&
        areEqual(currentPart.alignment.z, nextPart.alignment.z)
    );
};

const havePartsChanged = (
    currentInstance: ArticleInstance | undefined,
    nextInstance: ArticleInstancePlacement
): boolean => {
    if (!currentInstance) {
        return true;
    }

    const currentPartNames = Object.keys(currentInstance.parts);
    if (currentPartNames.length !== nextInstance.parts.length) {
        return true;
    }

    for (const nextPart of nextInstance.parts) {
        const currentPart = currentInstance.parts[nextPart.name];
        if (!currentPart || !arePartPlacementsEqual(currentPart, nextPart)) {
            return true;
        }
    }

    return false;
};

const hasPoiRelationChanged = (
    currentInstance: ArticleInstance | undefined,
    nextInstance: ArticleInstancePlacement
): boolean => {
    if (!currentInstance) {
        return false;
    }
    const currentPoiGuids = currentInstance.poiGuids ?? [];
    const nextPoiGuids = nextInstance.poiGuids ?? [];
    return currentPoiGuids.length !== nextPoiGuids.length || currentPoiGuids.some((poiGuid) => !nextPoiGuids.includes(poiGuid));
};

const getValidPoiGuids = (poiGuids: string[] | undefined): string[] => [
    ...new Set((poiGuids ?? []).filter((poiGuid) => poiGuid && poiGuid !== EMPTY_GUID))
];

const processArticleGroups = (
    articleGroups: ArticleGroupPlacement[],
    state: PlacementState
): { articles: typeof state.articles; pois: typeof state.pois } => {
    let updatedArticles = state.articles;
    let updatedPois = state.pois;
    const poiAdditions = new Map<number, Map<string, Set<string>>>();
    const poiRemovals = new Map<number, Set<string>>();
    const updatedInstanceIds: string[] = [];

    let hasArticleChanges = false;
    let hasPoiChanges = false;

    let poiGuidToIndex: Map<string, number> | null = null;
    let instanceToPoiIndices: Map<string, Set<number>> | null = null;

    const getPoiIndex = (poiGuid: string): number | undefined => {
        if (!poiGuidToIndex) {
            poiGuidToIndex = new Map<string, number>();
            state.pois.forEach((poiData, index) => {
                poiGuidToIndex!.set(poiData.poi.poiGuid, index);
            });
        }
        return poiGuidToIndex.get(poiGuid);
    };
    const getInstancePoiIndices = (instanceId: string): Set<number> => {
        if (!instanceToPoiIndices) {
            instanceToPoiIndices = new Map<string, Set<number>>();
            state.pois.forEach((poiData, index) => {
                Object.values(poiData.articles).forEach((instanceIds) => {
                    instanceIds.forEach((id) => {
                        const poiIndices = instanceToPoiIndices!.get(id) ?? new Set<number>();
                        poiIndices.add(index);
                        instanceToPoiIndices!.set(id, poiIndices);
                    });
                });
            });
        }

        return instanceToPoiIndices.get(instanceId) ?? new Set<number>();
    };

    const copyArticlesState = () => {
        if (!hasArticleChanges) {
            updatedArticles = { ...state.articles };
            hasArticleChanges = true;
        }
    };

    const copyPoiState = () => {
        if (!hasPoiChanges) {
            updatedPois = [...state.pois];
            hasPoiChanges = true;
        }
    };

    articleGroups.forEach((articleGroup) => {
        let article = updatedArticles[articleGroup.articleGuid];

        if (!article) {
            copyArticlesState();
            article = {
                articleNumber: articleGroup.articleNumber,
                articleInstances: {},
                edgeGeometries: null,
                lifeCycleCode: mapIndexToArticleLifeCycle(
                    articleGroup.lifeCycleCode !== undefined
                        ? (articleGroup.lifeCycleCode as unknown as number)
                        : undefined
                ),
                meshesCount: 0,
                meshInfos: [],
                partNames: []
            };
            updatedArticles[articleGroup.articleGuid] = article;
        }

        let updatedInstances = article.articleInstances;
        let articleInstancesChanged = false;

        articleGroup.instances.forEach((instanceData) => {
            const currentInstance = article!.articleInstances[instanceData.id];
            const previousPoiIndices = getInstancePoiIndices(instanceData.id);
            const targetPoiIndices = (instanceData.poiGuids ?? [])
                .map((poiGuid) => getPoiIndex(poiGuid))
                .filter((poiIndex): poiIndex is number => poiIndex !== undefined)
                .flatMap((poiIndex) => getRelatedPoiIndices(state.pois, poiIndex));
            const partsChanged = havePartsChanged(currentInstance, instanceData);
            const poiGuidChanged = hasPoiRelationChanged(currentInstance, instanceData);
            const instanceChanged = partsChanged || poiGuidChanged;
            const isNewInstanceWithPoi = !currentInstance && targetPoiIndices.length > 0;
            const shouldUpdatePoiRelation = poiGuidChanged || isNewInstanceWithPoi;

            if (!partsChanged && !shouldUpdatePoiRelation) {
                return;
            }

            if (!articleInstancesChanged && instanceChanged) {
                copyArticlesState();
                updatedInstances = { ...article!.articleInstances };
                articleInstancesChanged = true;
            }

            if (shouldUpdatePoiRelation) {
                previousPoiIndices.forEach((previousPoiIndex) => {
                    if (targetPoiIndices.includes(previousPoiIndex)) {
                        return;
                    }

                    if (!poiRemovals.has(previousPoiIndex)) {
                        poiRemovals.set(previousPoiIndex, new Set());
                    }
                    poiRemovals.get(previousPoiIndex)!.add(instanceData.id);
                });
            }

            if (shouldUpdatePoiRelation && targetPoiIndices.length > 0) {
                const relatedPoiIndices = [...new Set(targetPoiIndices)];
                relatedPoiIndices.forEach((relatedPoiIndex) => {
                    if (!poiAdditions.has(relatedPoiIndex)) {
                        poiAdditions.set(relatedPoiIndex, new Map());
                    }
                    const poiArticles = poiAdditions.get(relatedPoiIndex)!;
                    if (!poiArticles.has(articleGroup.articleGuid)) {
                        poiArticles.set(articleGroup.articleGuid, new Set());
                    }
                    poiArticles.get(articleGroup.articleGuid)!.add(instanceData.id);
                });

                if (instanceToPoiIndices) {
                    instanceToPoiIndices.set(instanceData.id, new Set(relatedPoiIndices));
                }
            }

            if (partsChanged) {
                const instance = mapPlacementToArticleInstance(instanceData);
                updatedInstances[instanceData.id] = instance;
                updatedInstanceIds.push(instanceData.id);
            } else if (poiGuidChanged && currentInstance) {
                updatedInstances[instanceData.id] = {
                    ...currentInstance,
                    poiGuids: getValidPoiGuids(instanceData.poiGuids)
                };
            }
        });

        if (articleInstancesChanged) {
            updatedArticles[articleGroup.articleGuid] = {
                ...article,
                articleInstances: updatedInstances
            };
        }
    });

    if (poiAdditions.size > 0 || poiRemovals.size > 0) {
        copyPoiState();
    }

    if (updatedInstanceIds.length > 0) {
        removeCachedInstanceEdges(updatedInstanceIds);
    }

    if (poiRemovals.size === 0 && poiAdditions.size === 0) {
        return { articles: updatedArticles, pois: state.pois };
    }

    poiRemovals.forEach((instanceIdsToRemove, poiIndex) => {
        const existingPoi = updatedPois[poiIndex];
        if (existingPoi) {
            const updatedArticleMap = { ...existingPoi.articles };

            Object.entries(updatedArticleMap).forEach(([articleGuid, instanceIds]) => {
                const filteredInstances = instanceIds.filter((id) => !instanceIdsToRemove.has(id));
                if (filteredInstances.length > 0) {
                    updatedArticleMap[articleGuid] = filteredInstances;
                } else {
                    delete updatedArticleMap[articleGuid];
                }
            });

            updatedPois[poiIndex] = {
                ...existingPoi,
                articles: updatedArticleMap
            };
        }
    });

    poiAdditions.forEach((articleMap, poiIndex) => {
        const existingPoi = updatedPois[poiIndex];
        if (existingPoi) {
            const updatedArticleMap = { ...existingPoi.articles };
            articleMap.forEach((instanceIdSet, articleGuid) => {
                const existingInstances = updatedArticleMap[articleGuid] ?? [];
                const existingSet = new Set(existingInstances);
                const merged = [...existingInstances];

                instanceIdSet.forEach((id) => {
                    if (!existingSet.has(id)) {
                        merged.push(id);
                    }
                });

                updatedArticleMap[articleGuid] = merged;
            });

            updatedPois[poiIndex] = {
                ...existingPoi,
                articles: updatedArticleMap
            };
        }
    });

    return { articles: updatedArticles, pois: updatedPois };
};

export const createConnectionSlice: StateCreator<PlacementState, [], [], ConnectionSliceState> = (set, get) => ({
    hubConnection: null,
    sessionId: EMPTY_GUID,
    isHubConnected: false,
    placementUpdate: null,

    stopHubConnection: async () => {
        const { hubConnection } = get();
        if (hubConnection) {
            isIntentionalStop = true;
            clearTimeout(reconnectTimerHandle);

            try {
                await hubConnection.stop();
            } catch (e) {
                console.error('SignalR - stopping connection failed:', e);
            }

            isIntentionalStop = false;
            set({ hubConnection: null, isHubConnected: false });
        }
    },

    startHubConnection: async (sessionId, getAccessToken) => {
        const state = get();

        if (state.hubConnection) {
            if (state.hubConnection.state === HubConnectionState.Connected) {
                if (state.sessionId !== sessionId) {
                    debug('Switching SignalR session group', { from: state.sessionId, to: sessionId });
                    await get().joinSessionGroup(state.hubConnection, sessionId);
                }
                return;
            }

            if (state.hubConnection.state !== HubConnectionState.Disconnected) {
                return;
            }
            set({ hubConnection: null });
        }

        const apiUrl = import.meta.env.VITE_APP_API_URL;
        const connection = new HubConnectionBuilder()
            .withUrl(`${apiUrl}formingSessionHub`, {
                accessTokenFactory: () => getAccessToken(),
                transport:
                    HttpTransportType.WebSockets | HttpTransportType.ServerSentEvents | HttpTransportType.LongPolling,
                skipNegotiation: false
            })
            .configureLogging(LogLevel.Information)
            .withStatefulReconnect()
            .withAutomaticReconnect(indefiniteRetryPolicy)
            .build();

        // set connection immediately to prevent duplicate calls
        set({ hubConnection: connection });

        connection.off(HubConnectionEvent.DELETE_ARTICLES);
        connection.off(HubConnectionEvent.PLACE_ARTICLE_GROUP);
        connection.off(HubConnectionEvent.PLACE_ARTICLE_GROUPS);

        get().registerHubConnectionEvents(connection, sessionId, getAccessToken);

        try {
            await connection.start();
            await get().joinSessionGroup(connection, sessionId);
        } catch (e) {
            const message = e instanceof Error ? e.message : '';
            if (message.includes('stopped during negotiation')) {
                debug('Connection start aborted (strict mode remount), ignoring.');
            } else {
                console.error('SignalR Connection Start failed:', e);
            }
            set({ hubConnection: null, isHubConnected: false });
        }
    },

    registerHubConnectionEvents: (connection, sessionId, getAccessToken) => {
        connection.on(HubConnectionEvent.DELETE_ARTICLES, (articleInstanceGuids: string[]) =>
            get().handleDeleteArticlesAsync(articleInstanceGuids)
        );
        connection.on(HubConnectionEvent.PLACE_ARTICLE_GROUP, (articleGroup: ArticleGroupPlacement) =>
            get().handlePlaceArticleGroupAsync(articleGroup)
        );
        connection.on(HubConnectionEvent.PLACE_ARTICLE_GROUPS, (articleGroups: ArticleGroupPlacement[]) =>
            get().handlePlaceArticleGroupsAsync(articleGroups)
        );

        connection.onreconnecting((error) => {
            debug('Connection lost, attempting to reconnect...', error);
            set({ isHubConnected: false });
            toastNotifications.warning({
                title: t('warning'),
                message: t('warningConnectionLost')
            });
        });

        connection.onreconnected(async () => {
            debug('Connection restored, re-joining group...');
            toastNotifications.success({
                title: t('success'),
                message: t('successReconnection')
            });
            await get().joinSessionGroup(connection, sessionId);
        });

        connection.onclose((error) => {
            // Ignore close events triggered by an intentional stopHubConnection() call
            if (isIntentionalStop) {
                return;
            }
            debug('Connection permanently closed', error);
            set({ hubConnection: null, isHubConnected: false });
            toastNotifications.error({
                title: t('error'),
                message: t('warningConnectionClosed')
            });

            debug(`Scheduling full connection restart in ${TIMEOUT_DELAY_MS / 1000}s...`);
            reconnectTimerHandle = setTimeout(() => {
                debug('Restarting hub connection...');
                get().startHubConnection(sessionId, getAccessToken);
            }, TIMEOUT_DELAY_MS);
        });
    },

    joinSessionGroup: async (connection, sessionId) => {
        try {
            await connection.invoke(HubConnectionEvent.SUBSCRIBE, sessionId, 1);
            debug('Successfully joined session group:', sessionId);
            set({ hubConnection: connection, sessionId, isHubConnected: true });
        } catch (e) {
            console.error('Failed to join session group:', e);
            set({ hubConnection: null, isHubConnected: false });
            toastNotifications.error({
                title: t('error'),
                message: t('errorJoinSession')
            });
        }
    },

    handlePlaceArticleGroupAsync: async (articleGroup) => {
        const articlesCount = articleGroup.instances.length;
        debug('PlaceArticleGroupAsync received', { articlesCount, articleGuid: articleGroup.articleGuid });
        processBatch([articleGroup], set, get);
    },

    handlePlaceArticleGroupsAsync: async (articleGroups) => {
        const articlesCount = articleGroups.reduce((sum, g) => sum + g.instances.length, 0);
        debug('PlaceArticleGroupsAsync received', { articlesCount, groups: articleGroups.length });
        processBatch(articleGroups, set, get);
    },

    handleDeleteArticlesAsync: async (articleInstanceGuids) => {
        const articlesCount = articleInstanceGuids.length;
        debug('DeleteArticlesAsync received', { articlesCount });

        if (articlesCount === 0) {
            return;
        }

        requestAnimationFrame(() => {
            set({
                placementUpdate: {
                    event: PlacementUpdateEvent.DELETE,
                    articleInstanceIds: new Set(articleInstanceGuids)
                }
            });
        });

        const currentSelections = get().articleInstanceSelections;
        const deletedSet = new Set(articleInstanceGuids);
        const remainingSelections = Object.fromEntries(
            Object.entries(currentSelections).filter(([id]) => !deletedSet.has(id))
        );
        if (Object.keys(remainingSelections).length !== Object.keys(currentSelections).length) {
            set({ articleInstanceSelections: remainingSelections });
        }

        set((state) => {
            const updatedArticles = { ...state.articles };
            let updatedPois = state.pois;

            articleInstanceGuids.forEach((articleInstanceGuid) => {
                let articleId: string | undefined;

                Object.entries(updatedArticles).forEach(([key, value]) => {
                    if (
                        Object.keys(value.articleInstances).find((instanceGuid) => instanceGuid === articleInstanceGuid)
                    ) {
                        articleId = key;
                    }
                });

                if (!articleId) {
                    return;
                }

                const article = updatedArticles[articleId];
                const updatedInstances = { ...article.articleInstances };
                delete updatedInstances[articleInstanceGuid];

                updatedArticles[articleId] = {
                    ...article,
                    articleInstances: updatedInstances
                };

                const poiIndex = updatedPois.findIndex((poiData) =>
                    Object.values(poiData.articles).some((articleInstanceIds) =>
                        articleInstanceIds.includes(articleInstanceGuid)
                    )
                );

                if (poiIndex !== -1) {
                    updatedPois = [...updatedPois];
                    const existingPoi = updatedPois[poiIndex];
                    const updatedArticleMap = { ...existingPoi.articles };

                    Object.entries(updatedArticleMap).forEach(([aid, instanceIds]) => {
                        const filtered = instanceIds.filter((id) => id !== articleInstanceGuid);
                        if (filtered.length > 0) {
                            updatedArticleMap[aid] = filtered;
                        } else {
                            delete updatedArticleMap[aid];
                        }
                    });

                    updatedPois[poiIndex] = {
                        ...existingPoi,
                        articles: updatedArticleMap
                    };
                }
            });

            return {
                articles: updatedArticles,
                pois: updatedPois
            };
        });

        removeCachedInstanceEdges(articleInstanceGuids);
        get().removeBatchInstances(articleInstanceGuids);
        get().removeNavigatorBatchInstances(articleInstanceGuids);
        articleInstanceGuids.forEach((id) => get().removeCollidersByRelation(id));
    }
});
